import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { StarRating } from '../../../components/ui/StarRating';
import { CommentComposer } from '../../../components/comments/CommentComposer';
import { CommentList } from '../../../components/comments/CommentList';
import type { Comment, WorkoutSession } from '../../../types';

interface CompletionScreenProps {
  session: WorkoutSession;
  sessionRating: number;
  onSessionRatingChange: (rating: number) => void;
  sessionNote: string;
  onSessionNoteChange: (note: string) => void;
  comments: Comment[];
  commentText: string;
  onCommentTextChange: (text: string) => void;
  onAddComment: () => void;
  onBack: () => void;
  onFinish: () => void;
  saving: boolean;
}

export function CompletionScreen({
  session,
  sessionRating,
  onSessionRatingChange,
  sessionNote,
  onSessionNoteChange,
  comments,
  commentText,
  onCommentTextChange,
  onAddComment,
  onBack,
  onFinish,
  saving,
}: CompletionScreenProps) {
  const navigate = useNavigate();

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card padding="lg" variant="elevated" className="space-y-4">
          <div className="space-y-1">
            <Badge variant="success" size="md" className="mb-2">
              You're done
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Great work!</h2>
            <p className="text-gray-600 dark:text-slate-400">
              Add a quick note or a progress photo before finishing.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                How was your workout?
              </h3>
              <div className="flex items-center gap-3">
                <StarRating value={sessionRating} onChange={onSessionRatingChange} size="lg" />
                {sessionRating > 0 && (
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    {sessionRating === 1 && 'Tough day'}
                    {sessionRating === 2 && 'Below average'}
                    {sessionRating === 3 && 'Good workout'}
                    {sessionRating === 4 && 'Great session'}
                    {sessionRating === 5 && 'Crushed it!'}
                  </span>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Session note
              </h3>
              <textarea
                data-testid="session-note-input"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition resize-none"
                placeholder="How did it feel? Wins, pains, anything your trainer should know."
                value={sessionNote}
                onChange={(e) => onSessionNoteChange(e.target.value)}
                rows={4}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Progress photos
              </h3>
              <Button variant="secondary" onClick={() => navigate('/client/posture-check')}>
                Open posture check
              </Button>
              {session.progressMedia && session.progressMedia.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {session.progressMedia.map((media, idx) => (
                    <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800">
                      {media.type === 'image' && (
                        <img
                          src={media.downloadUrl || media.externalUrl}
                          alt={media.caption || ''}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {media.type === 'video' && (
                        <video
                          src={media.downloadUrl || media.externalUrl}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={onBack}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              variant="success"
              size="lg"
              data-testid="session-finish-button"
              onClick={onFinish}
              isLoading={saving}
              className="flex-1"
            >
              {saving ? 'Saving...' : 'Finish Session'}
            </Button>
          </div>
        </Card>

        {/* Comments Section */}
        <Card padding="lg" variant="elevated" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Comments</h2>
            <Badge variant="default">{comments.length}</Badge>
          </div>

          <CommentComposer
            commentText={commentText}
            onChange={onCommentTextChange}
            onSubmit={onAddComment}
          />

          <div className="mt-4">
            <CommentList comments={comments} />
          </div>
        </Card>
      </div>
    </div>
  );
}
